import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import deburr from "lodash/deburr";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import { TAG_TYPES } from "constants/constants";

// Same normalisation as the popup, and as the accent-insensitive server search.
const normalize = (text = "") => deburr(text).toLowerCase();

// The popup tells options apart by `value`. Clients and prospections are two id
// sequences, so the type joins the id; free text is identified by its own text.
const optionValue = ({ type, value }) => `${type}:${value}`;

// Client field: search + tag selection (EOS InputSearchTag component). No
// checkbox: free text is offered as an extra tag option reusing the typed text.
// Everything but a reference client lands in client_name_freetext, prospections included.
const FafClientInput = ({ id, need, onChange, getClients, dataTestId }) => {
  const [options, setOptions] = useState([]);
  const latestTerm = useRef("");
  // A saved need only keeps the name, so a known entity would read as plain free
  // text: its profile is kept here, and looked up again when the need is reopened.
  const [freetextType, setFreetextType] = useState(null);

  // Reopened need: the stored name alone cannot tell a known entity from a typed
  // one, so it is searched once. Out of the user's scope, it stays free text.
  const hydratedName = useRef(null);
  useEffect(() => {
    const name = need.client_name_freetext;
    if (!name || hydratedName.current === name) return;
    hydratedName.current = name;
    let cancelled = false;
    getClients(name).then((found) => {
      const match = found.find((e) => normalize(e.label) === normalize(name));
      if (!cancelled && match) setFreetextType(match.type);
    });
    return () => {
      cancelled = true;
    };
  }, [need.client_name_freetext]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (term) => {
    latestTerm.current = term;
    const found = await getClients(term);
    // A slower earlier response must not overwrite the current term's options.
    if (latestTerm.current !== term) return;
    const clients = found.map((client) => ({
      ...client,
      value: optionValue(client),
      entityId: client.value,
    }));
    const trimmed = (term || "").trim();
    // The scoped list is narrow: free text stays reachable, last, unless already listed.
    // No `type` → grey tag ("standard"), to distinguish it from a reference entity.
    const alreadyListed = clients.some(
      (client) => normalize(client.label) === normalize(trimmed)
    );
    setOptions(
      trimmed && !alreadyListed
        ? [...clients, { value: trimmed, label: trimmed }]
        : clients
    );
  };

  // Real client → orange tag (type "clients"); free text → grey tag (no type).
  const selectedOptions = need.client_id
    ? [
        {
          value: optionValue({
            type: TAG_TYPES.CLIENTS,
            value: need.client_id,
          }),
          entityId: need.client_id,
          label: need.client_label,
          type: TAG_TYPES.CLIENTS,
        },
      ]
    : need.client_name_freetext
    ? [
        {
          value: need.client_name_freetext,
          label: need.client_name_freetext,
          ...(freetextType && { type: freetextType }),
        },
      ]
    : [];

  return (
    <InputSearchTag
      id={`faf-need-client-${id}`}
      dataTestId={dataTestId}
      minLength={2}
      limitChoice={1}
      serverFiltered
      placeholder={{
        id: "tools_manager_faf_need_client_ph",
        defaultMessage: "Rechercher un client ou saisir un nom",
      }}
      popup={{
        title: { id: "tools_manager_faf_need_client", defaultMessage: "Client" },
      }}
      options={options}
      selectedOptions={selectedOptions}
      getOptions={search}
      setValue={(sel) => {
        const opt = sel[0];
        if (!opt) return;
        setFreetextType(opt.type ?? null);
        // `client_id` is a FK to the client table: only a reference client may fill it.
        if (opt.type === TAG_TYPES.CLIENTS)
          onChange({
            client_id: opt.entityId,
            client_label: opt.label,
            client_name_freetext: null,
          });
        else
          onChange({
            client_id: null,
            client_label: null,
            client_name_freetext: opt.label,
          });
      }}
      onDelete={() => {
        setFreetextType(null);
        onChange({
          client_id: null,
          client_label: null,
          client_name_freetext: null,
        });
      }}
    />
  );
};

FafClientInput.propTypes = {
  id: PropTypes.string,
  need: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  dataTestId: PropTypes.string,
};

export default FafClientInput;
